import { quoteWith } from '../model/capabilities';
import type { Outcome } from '../model/types';

export type OutcomeSort = 'probability' | 'volume' | 'flow';

export const MAX_SHOWN_ACTIVE = 6;
export const MAX_SHOWN_CLOSED = 3;

export function probabilityOf(outcome: Outcome): number | null {
    return quoteWith(outcome, 'probability')?.probability ?? null;
}

export function volumeOf(outcome: Outcome): number {
    return outcome.quotes.reduce((sum, q) => sum + (q.volume ?? 0), 0);
}

export function isClosed(outcome: Outcome): boolean {
    return outcome.quotes.length > 0 && outcome.quotes.every(q => q.closed);
}

export type FlowRank = (outcome: Outcome) => number;

const NO_FLOW: FlowRank = () => 0;

export function compareOutcomes(
    sort: OutcomeSort,
    flowRank: FlowRank = NO_FLOW
): (a: Outcome, b: Outcome) => number {
    const byProbability = (a: Outcome, b: Outcome) =>
        (probabilityOf(b) ?? 0) - (probabilityOf(a) ?? 0);
    if (sort === 'volume') return (a, b) => volumeOf(b) - volumeOf(a);
    if (sort === 'flow') {
        return (a, b) => flowRank(b) - flowRank(a) || byProbability(a, b);
    }
    return byProbability;
}

export function orderOutcomes(
    outcomes: readonly Outcome[],
    sort: OutcomeSort,
    flowRank: FlowRank = NO_FLOW
): Outcome[] {
    const compare = compareOutcomes(sort, flowRank);
    const open = outcomes.filter(o => !isClosed(o)).sort(compare);
    const closed = outcomes.filter(o => isClosed(o)).sort(compare);
    return [...open, ...closed];
}

export interface OutcomeSplit {
    shown: Outcome[];
    hidden: Outcome[];
}

export function splitOutcomes(
    outcomes: readonly Outcome[],
    sort: OutcomeSort,
    flowRank: FlowRank = NO_FLOW
): OutcomeSplit {
    const ordered = orderOutcomes(outcomes, sort, flowRank);
    const anyOpen = ordered.some(o => !isClosed(o));
    const limit = anyOpen ? MAX_SHOWN_ACTIVE : MAX_SHOWN_CLOSED;
    return { shown: ordered.slice(0, limit), hidden: ordered.slice(limit) };
}
